import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useGroupData, addGroupTodo, updateGroupTodo, deleteGroupTodo } from '../../hooks/useGroups';
import Modal from '../../components/ui/Modal';
import { fmtDate } from '../../lib/constants';

const STATUS_COLOR = { pending: 'var(--text3)', in_progress: 'var(--blue)', done: 'var(--success)' };
const STATUS_BG    = { pending: 'badge-gray', in_progress: 'badge-blue', done: 'badge-green' };

export default function GroupTodos() {
  const { profile } = useAuth();
  const { group, todos, loading, refreshTodos } = useGroupData(profile?.group_id);
  const [showAdd, setShowAdd] = useState(false);
  const [filter,  setFilter]  = useState('all');
  const [form,    setForm]    = useState({ title: '', description: '', due_date: '' });
  const [saving,  setSaving]  = useState(false);

  const filtered = todos.filter(t => filter === 'all' || t.status === filter);
  const counts   = { pending: todos.filter(t => t.status === 'pending').length, in_progress: todos.filter(t => t.status === 'in_progress').length, done: todos.filter(t => t.status === 'done').length };

  async function handleAdd(e) {
    e.preventDefault(); setSaving(true);
    const todo = await addGroupTodo({ ...form, group_id: profile.group_id, status: 'pending' });
    if (todo) { setShowAdd(false); setForm({ title: '', description: '', due_date: '' }); refreshTodos(); }
    setSaving(false);
  }

  async function handleStatus(id, status) {
    await updateGroupTodo(id, { status });
    refreshTodos();
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this task?')) return;
    await deleteGroupTodo(id);
    refreshTodos();
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Loading…</div>;

  return (
    <div>
      <div className="flex-between mb-4">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800 }}>✅ {group?.name} To-Do List</h1>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>Track tasks and things done by the group</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Task</button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {[['all', 'All', todos.length], ['pending', 'Pending', counts.pending], ['in_progress', 'In Progress', counts.in_progress], ['done', 'Done', counts.done]].map(([v, l, c]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`btn btn-sm ${filter === v ? 'btn-primary' : 'btn-ghost'}`}
            style={{ border: filter === v ? '' : '1.5px solid var(--border)' }}>
            {l} <span style={{ fontSize: 11, opacity: .8 }}>({c})</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.length === 0 && (
          <div className="alert alert-info">
            {filter === 'done' ? '🎉 No completed tasks yet.' : filter === 'pending' ? '👍 No pending tasks!' : 'No tasks found. Add your first task.'}
          </div>
        )}
        {filtered.map(t => (
          <div key={t.id} className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: STATUS_COLOR[t.status], flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: t.status === 'done' ? 400 : 700, textDecoration: t.status === 'done' ? 'line-through' : 'none', color: t.status === 'done' ? 'var(--text3)' : 'var(--text)', fontSize: 14 }}>{t.title}</div>
              {t.description && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{t.description}</div>}
              {t.due_date && <div style={{ fontSize: 11, color: new Date(t.due_date) < new Date() && t.status !== 'done' ? 'var(--danger)' : 'var(--text3)', marginTop: 3 }}>📅 Due: {fmtDate(t.due_date)}</div>}
            </div>
            <span className={`badge ${STATUS_BG[t.status]}`}>{t.status.replace('_', ' ')}</span>
            <div className="flex gap-1">
              {t.status === 'pending'     && <button className="btn btn-sm btn-blue"    onClick={() => handleStatus(t.id, 'in_progress')}>Start</button>}
              {t.status === 'in_progress' && <button className="btn btn-sm btn-success" onClick={() => handleStatus(t.id, 'done')}>✓ Done</button>}
              {t.status === 'done'        && <button className="btn btn-sm btn-ghost"   onClick={() => handleStatus(t.id, 'pending')}>Reopen</button>}
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t.id)}>×</button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Modal */}
      {showAdd && (
        <Modal title="Add Task" onClose={() => setShowAdd(false)}
          footer={<><button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button><button className="btn btn-primary" form="todo-form" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Add Task'}</button></>}>
          <form id="todo-form" onSubmit={handleAdd}>
            <div className="form-group"><label className="form-label">Task Title *</label><input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="What needs to be done?" required /></div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Optional details…" /></div>
            <div className="form-group"><label className="form-label">Due Date</label><input className="form-input" type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
